import React, { useState } from 'react';
import { CalendarClock, Check, Mail, MapPin, Phone, Truck } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SocialLinks } from '../components/SocialLinks';
import { CONTACT_EMAIL, CONTACT_EMAIL_HREF, CONTACT_PHONE_DISPLAY, CONTACT_PHONE_HREF, DELIVERY_CITIES, PICKUP_ADDRESS, PICKUP_MAP_URL } from '../data/contact';
import { useLanguage } from '../contexts/LanguageContext';
export function Contact() {
  const {
    t
  } = useLanguage();
  const [isSent, setIsSent] = useState(false);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSent(true);
    window.setTimeout(() => setIsSent(false), 4000);
  };
  const inputClass = 'w-full border border-caramel/30 bg-creme px-4 py-3 font-sans text-sm text-cacao-900 placeholder:text-cacao-700/40 transition-colors focus:border-caramel focus:outline-none';
  const labelClass = 'mb-2 block font-sans text-[11px] uppercase tracking-[0.16em] text-cacao-800';
  return <>
      <PageHeader eyebrow={t.contact.eyebrow} titleLead={t.contact.titleLead} titleAccent={t.contact.titleAccent} intro={t.contact.intro} data-id="element-299" data-mp="15dch3lgoww65" />

      <section className="bg-creme py-20 md:py-28" data-id="element-300" data-mp="1e8ad51f4wp1p">
        <div className="mx-auto max-w-7xl px-6 md:px-12" data-id="element-301" data-mp="1j6k7j9t1blm9">
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20" data-id="element-302" data-mp="1j620rj8qxp03">
            <div data-id="element-303" data-mp="mhmatm1q5dmg">
              <ul className="space-y-8" data-id="element-304" data-mp="mi5kkfh80vpe">
                <li className="flex items-start gap-5 border-b border-caramel/20 pb-8" data-id="element-305" data-mp="qi6aoxdomvca">
                  <Phone size={22} strokeWidth={1.25} className="mt-1 shrink-0 text-caramel" aria-hidden="true" data-id="element-306" data-mp="qjzfe74qj5hn" />
                  <div data-id="element-307" data-mp="26wob5h683ld9">
                    <p className="mb-1 font-sans text-[11px] uppercase tracking-[0.18em] text-or" data-id="element-308" data-mp="26x7gzzilmjax">
                      {t.contact.phoneLabel}
                    </p>
                    <a href={CONTACT_PHONE_HREF} className="font-serif text-xl text-cacao-900 transition-colors hover:text-caramel" data-id="element-309" data-mp="1m7da59f0r7ds">
                      {CONTACT_PHONE_DISPLAY}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-5 border-b border-caramel/20 pb-8" data-id="element-310" data-mp="1r6q0w590gyln">
                  <Mail size={22} strokeWidth={1.25} className="mt-1 shrink-0 text-caramel" aria-hidden="true" data-id="element-311" data-mp="13gvp7r0fhg2z" />
                  <div className="min-w-0" data-id="element-312" data-mp="1d772i46051mj">
                    <p className="mb-1 font-sans text-[11px] uppercase tracking-[0.18em] text-or" data-id="element-313" data-mp="1d88dk8n0po49">
                      {t.contact.emailLabel}
                    </p>
                    <a href={CONTACT_EMAIL_HREF} className="break-all font-serif text-lg text-cacao-900 transition-colors hover:text-caramel md:text-xl" data-id="element-314" data-mp="kpkwr5f20v8z">
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-5 border-b border-caramel/20 pb-8" data-id="element-315" data-mp="2w6xxf2gkhijg">
                  <MapPin size={22} strokeWidth={1.25} className="mt-1 shrink-0 text-caramel" aria-hidden="true" data-id="element-316" data-mp="2hhs0jl57c4eq" />
                  <div data-id="element-317" data-mp="3a5ntqfvkbe6r">
                    <p className="mb-1 font-sans text-[11px] uppercase tracking-[0.18em] text-or" data-id="element-318" data-mp="3a3bfacp0jlyg">
                      {t.contact.pickupLabel}
                    </p>
                    <a href={PICKUP_MAP_URL} target="_blank" rel="noopener noreferrer" className="font-serif text-lg text-cacao-900 transition-colors hover:text-caramel" data-id="element-319" data-mp="313wqriseplgl">
                      {PICKUP_ADDRESS}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-5 border-b border-caramel/20 pb-8" data-id="element-320" data-mp="29z2gf1le3ck2">
                  <Truck size={22} strokeWidth={1.25} className="mt-1 shrink-0 text-caramel" aria-hidden="true" data-id="element-321" data-mp="257127a4nvbre" />
                  <div data-id="element-322" data-mp="1vafswcllzehr">
                    <p className="mb-2 font-sans text-[11px] uppercase tracking-[0.18em] text-or" data-id="element-323" data-mp="1vaxvrs2s0qyn">
                      {t.contact.deliveryLabel}
                    </p>
                    <ul className="mb-3 flex flex-wrap gap-2" data-id="element-324" data-mp="2nz3kepwyb03r">
                      {DELIVERY_CITIES.map(city => <li key={city} className="border border-caramel/40 px-3 py-1 font-sans text-[10px] uppercase tracking-[0.12em] text-cacao-800" data-id="element-325" data-mp="2iww2ljpfwpti">
                          {city}
                        </li>)}
                    </ul>
                    <p className="font-sans text-sm font-light text-cacao-700" data-id="element-326" data-mp="3f5k8ki2ii67f">
                      {t.orderInfo.deliveryNote}
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-5" data-id="element-327" data-mp="3o68o5h7k0emf">
                  <CalendarClock size={22} strokeWidth={1.25} className="mt-1 shrink-0 text-caramel" aria-hidden="true" data-id="element-328" data-mp="3o4fcfni3347k" />
                  <div data-id="element-329" data-mp="1sibu86lf0rr">
                    <p className="mb-1 font-sans text-[11px] uppercase tracking-[0.18em] text-or" data-id="element-330" data-mp="6p8p6nr0ji9h">
                      {t.contact.ordersLabel}
                    </p>
                    <p className="font-serif text-lg text-cacao-900" data-id="element-331" data-mp="aozk93nkokjt">
                      {t.contact.ordersValue}
                    </p>
                  </div>
                </li>
              </ul>

              <div className="mt-10" data-id="element-332" data-mp="2ckjnd6vmk6qo">
                <p className="mb-4 font-sans text-[11px] uppercase tracking-[0.18em] text-or" data-id="element-333" data-mp="2hh0gygllfdb5">
                  {t.contact.followTitle}
                </p>
                <SocialLinks variant="dark" data-id="element-334" data-mp="3kujzeeadxsol" />
              </div>
            </div>

            <div className="border border-caramel/25 bg-creme-dark p-8 md:p-10" data-id="element-335" data-mp="3fv7bdq7s2fkf">
              <h2 className="mb-8 font-serif text-2xl text-cacao-900" data-id="element-336" data-mp="3ttx9n96c2lad">
                {t.contact.form.title}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-337" data-mp="3ow5jajlc778a">
                <div data-id="element-338" data-mp="7kmk43gxzfhj">
                  <label htmlFor="contact-name" className={labelClass} data-id="element-339" data-mp="7k344saur165">
                    {t.contact.form.name}
                  </label>
                  <input id="contact-name" name="name" type="text" required className={inputClass} data-id="element-340" data-mp="gdzihkxrz9b0" />
                </div>

                <div className="grid gap-6 sm:grid-cols-2" data-id="element-341" data-mp="qcx6mmrah0th">
                  <div data-id="element-342" data-mp="ldubpv50waz8">
                    <label htmlFor="contact-email" className={labelClass} data-id="element-343" data-mp="ledm8qscfx38">
                      {t.contact.form.email}
                    </label>
                    <input id="contact-email" name="email" type="email" required className={inputClass} data-id="element-344" data-mp="14x9qwj763hqi" />
                  </div>
                  <div data-id="element-345" data-mp="zypzi39t1dqr">
                    <label htmlFor="contact-phone" className={labelClass} data-id="element-346" data-mp="10245dlhog52y">
                      {t.contact.form.phone}
                    </label>
                    <input id="contact-phone" name="phone" type="tel" className={inputClass} data-id="element-347" data-mp="qzl2x2hszuh8" />
                  </div>
                </div>

                <div data-id="element-348" data-mp="h7fcvyb43n4o">
                  <label htmlFor="contact-message" className={labelClass} data-id="element-349" data-mp="39qqun5b69y1">
                    {t.contact.form.message}
                  </label>
                  <textarea id="contact-message" name="message" rows={5} required placeholder={t.contact.form.messagePlaceholder} className={`${inputClass} resize-none`} data-id="element-350" data-mp="7a2bhzxo8a9i" />
                </div>

                <button type="submit" className={`flex w-full items-center justify-center gap-3 py-4 font-sans text-xs uppercase tracking-[0.16em] transition-colors duration-300 ${isSent ? 'bg-caramel text-creme' : 'bg-cacao-900 text-creme hover:bg-caramel'}`} data-id="element-351" data-mp="durvs7eix53c">
                  {isSent && <Check size={16} strokeWidth={2} aria-hidden="true" data-id="element-352" data-mp="3m8qon0678acm" />}
                  {t.contact.form.send}
                </button>

                <p aria-live="polite" className="text-center font-sans text-xs font-light text-cacao-700" data-id="element-353" data-mp="3h8mctng8k3sq">
                  {t.contact.form.note}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>;
}